import { type ReactElement, useState } from 'react';
import type { CustodyEventKind, CustodyHolder, DeliveryStatus, DeliveryView, ProofInput } from '@vello/api-client';
import { Button, Field, Notice, Select, TextInput, Text, Stack, Row } from '@vello/web-theme';
import { grantsAllow, sendInterventions, sendProof } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
const KINDS: readonly CustodyEventKind[] = ['pickup', 'scan', 'handoff', 'delivery', 'return'];
const HOLDERS: readonly CustodyHolder[] = ['sender', 'courier', 'recipient', 'hub'];
const METHODS: readonly ProofInput['method'][] = ['pin', 'photo', 'signature'];

type Action = 'status' | 'confirm' | 'deliver' | 'return' | 'custody';

function newId(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function SendInterventionPanel({ delivery, onChanged }: { readonly delivery: DeliveryView; readonly onChanged: () => void }): ReactElement | null {
  const { runtime, grants } = useAdmin();
  const { t } = useT();
  const { v, fail, refusal: refusalOf } = useVocab();
  const may = grants !== undefined && grantsAllow(grants, { permission: 'delivery:manage', target: 'PLATFORM' });
  const options = sendInterventions(delivery.status);
  const actions: Action[] = [
    ...(options.mayConfirm ? (['confirm'] as const) : []),
    ...(options.statuses.length > 0 ? (['status'] as const) : []),
    ...(options.mayDeliver ? (['deliver'] as const) : []),
    ...(options.mayReturn ? (['return'] as const) : []),
    ...(options.mayRecordCustody ? (['custody'] as const) : []),
  ];
  const [action, setAction] = useState<Action | undefined>(undefined);
  const [to, setTo] = useState<DeliveryStatus | undefined>(undefined);
  const [method, setMethod] = useState<ProofInput['method']>('pin');
  const [reference, setReference] = useState('');
  const [kind, setKind] = useState<CustodyEventKind>('handoff');
  const [from, setFrom] = useState<CustodyHolder>('courier');
  const [holder, setHolder] = useState<CustodyHolder>('recipient');
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ readonly tone: 'success' | 'danger'; readonly text: string } | undefined>(undefined);

  if (!may || actions.length === 0) return null;
  const current: Action = action !== undefined && actions.includes(action) ? action : (actions[0] as Action);
  const target: DeliveryStatus | undefined = to !== undefined && options.statuses.includes(to) ? to : options.statuses[0];

  const summary = ((): string => {
    switch (current) {
      case 'confirm': return t('cdesk.send.sum.confirm');
      case 'status': return t('cdesk.send.sum.status', { status: target !== undefined ? v('delivery.status', target) : '' });
      case 'deliver': return t('cdesk.send.sum.deliver');
      case 'return': return t('cdesk.send.sum.return');
      case 'custody': return t('cdesk.send.sum.custody', { kind: v('delivery.custody_event_kind', kind), from: v('delivery.custody_holder', from), to: v('delivery.custody_holder', holder) });
    }
  })();
  const needsProof = current === 'deliver' || current === 'return';
  const proof = needsProof ? sendProof({ method, reference, at: new Date().toISOString() }) : undefined;
  const ready = !(needsProof && proof === undefined) && !(current === 'status' && target === undefined);

  const run = async (): Promise<void> => {
    setBusy(true);
    setNotice(undefined);
    const res =
      current === 'confirm' ? await runtime.delivery.confirm(delivery.deliveryId)
        : current === 'status' && target !== undefined ? await runtime.delivery.advance(delivery.deliveryId, target)
          : current === 'deliver' && proof !== undefined ? await runtime.delivery.deliver(delivery.deliveryId, proof)
            : current === 'return' && proof !== undefined ? await runtime.delivery.return(delivery.deliveryId, proof)
              : await runtime.delivery.recordCustody(delivery.deliveryId, { eventId: newId(), kind, from, to: holder });
    setBusy(false);
    setArmed(false);
    if (res.ok) {
      setNotice({ tone: 'success', text: t('cdesk.send.done') });
      setReference('');
      onChanged();
    } else {
      setNotice({ tone: 'danger', text: refusalOf('refusal.admin.send_intervention', res.error) ?? t('lmap.err.generic', { code: fail(res.error) }) });
    }
  };

  return (
    <Stack as="section" gap={3} aria-labelledby="send-intervene-title">
      <Text as="h3" role="label" id="send-intervene-title">{t('cdesk.send.title')}</Text>
      <Text as="p" role="caption" tone="secondary">{t('cdesk.send.hint')}</Text>
      {notice !== undefined && <div><Notice tone={notice.tone}>{notice.text}</Notice></div>}
      <Stack gap={3}>
        <Field label={t('cdesk.send.action')} htmlFor="send-action">
          <Select id="send-action" value={current} onChange={(e) => { setAction(e.target.value as Action); setArmed(false); }}>
            {actions.map((a) => (
              <option key={a} value={a}>{v('central_desk.send_intervention_action', a)}</option>
            ))}
          </Select>
        </Field>
        {current === 'status' && (
          <Field label={t('cdesk.send.statusTo')} htmlFor="send-status-to">
            <Select id="send-status-to" value={target ?? ''} onChange={(e) => { setTo(e.target.value as DeliveryStatus); setArmed(false); }}>
              {options.statuses.map((s) => (
                <option key={s} value={s}>{v('delivery.status', s)}</option>
              ))}
            </Select>
          </Field>
        )}
        {needsProof && (
          <>
            <Field label={t('cdesk.send.proofMethod')} htmlFor="send-proof-method">
              <Select id="send-proof-method" value={method} onChange={(e) => { setMethod(e.target.value as ProofInput['method']); setArmed(false); }}>
                {METHODS.map((m) => (
                  <option key={m} value={m}>{v('courier.proof_method', m)}</option>
                ))}
              </Select>
            </Field>
            <Field label={t(method === 'pin' ? 'cdesk.send.proofPin' : 'cdesk.send.proofRef')} htmlFor="send-proof-ref">
              <TextInput id="send-proof-ref" className="tnum" value={reference} autoComplete="off" onChange={(e) => { setReference(e.target.value); setArmed(false); }} />
            </Field>
          </>
        )}
        {current === 'custody' && (
          <Row gap={3} align="stretch" wrap>
            <Field label={t('cdesk.send.custodyKind')} htmlFor="send-custody-kind">
              <Select id="send-custody-kind" value={kind} onChange={(e) => setKind(e.target.value as CustodyEventKind)}>
                {KINDS.map((k) => (
                  <option key={k} value={k}>{v('delivery.custody_event_kind', k)}</option>
                ))}
              </Select>
            </Field>
            <Field label={t('cdesk.send.custodyFrom')} htmlFor="send-custody-from">
              <Select id="send-custody-from" value={from} onChange={(e) => setFrom(e.target.value as CustodyHolder)}>
                {HOLDERS.map((h) => (
                  <option key={h} value={h}>{v('delivery.custody_holder', h)}</option>
                ))}
              </Select>
            </Field>
            <Field label={t('cdesk.send.custodyTo')} htmlFor="send-custody-to">
              <Select id="send-custody-to" value={holder} onChange={(e) => setHolder(e.target.value as CustodyHolder)}>
                {HOLDERS.map((h) => (
                  <option key={h} value={h}>{v('delivery.custody_holder', h)}</option>
                ))}
              </Select>
            </Field>
          </Row>
        )}
        {armed && <Notice tone="neutral">{t('cdesk.send.confirmPrompt', { summary })}</Notice>}
        <Row gap={3} align="stretch">
          {!armed ? (
            <Button variant="primary" disabled={busy || !ready} onClick={() => setArmed(true)}>{t('cdesk.send.apply')}</Button>
          ) : (
            <>
              <Button variant="primary" disabled={busy} onClick={() => void run()}>{t('cdesk.send.confirm')}</Button>
              <Button variant="ghost" disabled={busy} onClick={() => setArmed(false)}>{t('ui.cancel')}</Button>
            </>
          )}
        </Row>
      </Stack>
    </Stack>
  );
}
